import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Wheat, MessageCircle, Phone, Instagram, Sparkles, ShieldCheck, CheckCircle2, ShoppingBag } from "lucide-react";
import { CONTACT, SHOPIER_URL, WHATSAPP_TRAINING_HREF } from "../lib/contact";
import ImageSlot from "../components/ImageSlot";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: "var(--em-cream)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-14 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-1 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" style={{ background: "var(--em-terracotta-light)", color: "var(--em-terracotta-hover)" }}>
              <MapPin size={13} /> <span className="font-semibold tracking-wide">Ataşehir, İstanbul</span>
            </div>
            <h1 className="font-serif mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] tracking-tight text-[color:var(--em-espresso)]" data-testid="hero-title">
              Doğal mayanın <br className="hidden sm:block" />
              <span className="italic" style={{ color: "var(--em-terracotta)" }}>sanatını</span> öğrenin.
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl text-[color:var(--em-espresso-soft)]">
              Ekşi Maya Gurmesi; teoriyi, uygulamayı ve ustalığı tek bir eğitim yolculuğunda birleştirir.
              Mutfağınızda gerçek ekmekler ortaya çıkarmayı adım adım öğretir.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/egitimler" className="btn-terracotta inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold" data-testid="hero-cta-trainings">
                Eğitimlere Katıl <ArrowRight size={16} />
              </Link>
              <a href={SHOPIER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold bg-[color:var(--em-espresso)] text-white hover:opacity-90 transition-opacity" data-testid="hero-cta-order">
                <ShoppingBag size={16} /> Sipariş Ver
              </a>
              <Link to="/iletisim" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-semibold border border-[color:var(--em-espresso)] text-[color:var(--em-espresso)] hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="hero-cta-contact">
                İletişime Geç
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: "6+", v: "yıllık ustalık" },
                { k: "1→1", v: "birebir eğitim" },
                { k: "%100", v: "doğal maya" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-serif text-2xl font-semibold text-[color:var(--em-espresso)]">{s.k}</div>
                  <div className="text-xs mt-1 text-[color:var(--em-text-mute)] leading-snug">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 order-2 lg:order-2 relative">
            <ImageSlot src="/images/atolye-2.webp" alt="Ekşi Maya Gurmesi atölyesinde ekşi mayalı ekmek" position="center 30%" className="aspect-[4/5] rounded-sm">
              <div className="absolute left-4 bottom-4 right-4 lg:left-6 lg:bottom-6 lg:right-6 p-5 lg:p-6 rounded-sm" style={{ background: "rgba(253, 251, 247, 0.92)", borderLeft: "3px solid var(--em-terracotta)" }}>
                <div className="eyebrow">Zanaat Notu</div>
                <p className="mt-2 font-serif text-base lg:text-lg italic text-[color:var(--em-espresso)]">
                  “İyi ekmek sabırla pişer. Hız değil, zaman onun en önemli malzemesidir.”
                </p>
              </div>
            </ImageSlot>
          </div>
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="border-y" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">Hakkımızda</div>
            <h2 className="font-serif mt-3 text-3xl lg:text-4xl font-semibold leading-tight">Ekmek bir sabır sanatıdır.</h2>
            <ImageSlot src="/images/filiz-kiratoglu.webp" alt="Bakery Chef Filiz Kıratoğlu, Ekşi Maya Gurmesi" position="center 75%" className="mt-8 aspect-[4/5] rounded-sm" />
          </div>
          <div className="lg:col-span-7 space-y-4 text-[color:var(--em-espresso-soft)] text-base leading-relaxed">
            <p>
              Ekşi Maya Gurmesi, geleneksel doğal maya kültürünü modern fermantasyon bilgisiyle
              buluşturan bir eğitim atölyesidir. Amacımız, öğrencilerimizin kendi mutfaklarında
              tutarlı sonuçlar alabilmesidir.
            </p>
            <p>
              Eğitimlerimiz başlangıç seviyesinden ileri seviyeye kadar uzanır. Her öğrenciye
              ihtiyacına göre kişiye özel rehberlik sunar; yerli unlar ve doğal maya ile çalışırız.
            </p>
            <div className="flex flex-wrap gap-6 pt-4">
              {[
                { icon: ShieldCheck, label: "Geleneksel maya kültürü" },
                { icon: Sparkles, label: "Kişiye özel rehberlik" },
                { icon: Wheat, label: "Yerli unlar & doğal maya" },
              ].map((it) => (
                <div key={it.label} className="inline-flex items-center gap-2 text-sm font-medium">
                  <it.icon size={16} style={{ color: "var(--em-terracotta)" }} /> {it.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRAININGS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28" data-testid="home-trainings-section">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
          <div>
            <div className="eyebrow">Eğitimler</div>
            <h2 className="font-serif mt-3 text-3xl lg:text-4xl font-semibold">Yolculuğunuza uygun formatı seçin.</h2>
          </div>
          <Link to="/egitimler" className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--em-terracotta-hover)] hover:underline" data-testid="home-trainings-detail-link">
            Tüm detaylar <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {[
            {
              tag: "Birebir • Ataşehir",
              title: "Yüz Yüze Eğitim",
              desc: "Ataşehir'de iki tam gün süren, teori ve uygulamayı birleştiren birebir eğitim. Başlangıçtan ileri seviyeye kişiye özel ilerleme; eğitim sonrasında online destek devam eder.",
              bullets: ["2 tam gün, birebir", "Teori + uygulamalı pratik", "Eğitim sonrası online destek"],
              testId: "home-training-face",
              img: "/images/atolye-1.webp",
              pos: "center 22%",
            },
            {
              tag: "Kişiye Özel Süreç",
              title: "Online Eğitim",
              desc: "Kapalı WhatsApp grubu ve Telegram üzerinden video dersler, tarifler ve sürekli destekle kendi mutfağınızda ilerlediğiniz kapsamlı bir eğitim.",
              bullets: ["Kapalı WhatsApp grubu", "Telegram'da video dersler & tarifler", "Kişiye özel ilerleme ve rehberlik"],
              testId: "home-training-online",
              img: "/images/ogrenci-4.webp",
              pos: "center 30%",
            },
          ].map((t) => (
            <article key={t.title} className="lift group relative overflow-hidden rounded-sm border" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }} data-testid={t.testId}>
              <ImageSlot src={t.img} alt={t.title} position={t.pos} className={`aspect-[16/10] ${t.img ? "" : "hidden md:block"}`} />
              <div className="p-7 lg:p-9">
                <div className="eyebrow">{t.tag}</div>
                <h3 className="font-serif mt-3 text-2xl font-semibold">{t.title}</h3>
                <p className="mt-3 text-[color:var(--em-espresso-soft)] leading-relaxed">{t.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {t.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={16} className="mt-0.5" style={{ color: "var(--em-terracotta)" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 text-sm text-[color:var(--em-text-mute)]">
                  Detaylı bilgi ve kayıt için iletişime geçin.
                </div>
                <div className="mt-5 flex gap-3">
                  <Link to="/egitim-basvuru" className="btn-terracotta inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold" data-testid={`${t.testId}-apply`}>
                    Başvuru Yap
                  </Link>
                  <a href={WHATSAPP_TRAINING_HREF} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid={`${t.testId}-whatsapp`}>
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* BREAD / ORDERS */}
      <section className="border-y" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">Ekmek Siparişi</div>
            <h2 className="font-serif mt-3 text-3xl lg:text-4xl font-semibold" data-testid="brand-slogan">
              Her lokmada bir hikâye.<sup className="ml-0.5 align-super text-[0.4em] font-normal">®</sup>
            </h2>
            <p className="mt-5 text-[color:var(--em-espresso-soft)] leading-relaxed">
              Doğal ekşi mayalı ekmeklerimizi Shopier mağazamız üzerinden sipariş edebilirsiniz.
            </p>
            <a href={SHOPIER_URL} target="_blank" rel="noopener noreferrer" className="btn-terracotta inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-full text-sm font-semibold" data-testid="home-orders-cta">
              <ShoppingBag size={16} /> Sipariş Ver
            </a>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {[
              { src: "/images/ekmek-yuvarlak.webp", alt: "Ekşi mayalı yuvarlak ekmek" },
              { src: "/images/ekmek-dilimler.webp", alt: "Ekşi mayalı ekmek dilimleri" },
              { src: "/images/ekmek-tohumlu.webp", alt: "Tohumlu ekşi mayalı ekmek" },
              { src: "/images/ekmek-butun.webp", alt: "Ekşi mayalı ekmek" },
              { src: "/images/ekmek-kesit.webp", alt: "Ekşi mayalı ekmek kesitleri" },
              { src: "/images/ekmek-cevizli-dilim.webp", alt: "Cevizli ekşi mayalı ekmek dilimleri" },
            ].map((p) => (
              <ImageSlot key={p.src} src={p.src} alt={p.alt} className="rounded-sm aspect-square" />
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24 text-center" data-testid="home-instagram-section">
        <div className="eyebrow">Instagram</div>
        <p className="mt-4 max-w-2xl mx-auto text-[color:var(--em-espresso-soft)]">
          Ekmeklerimizi, atölyemizdeki çalışmaları ve eğitim duyurularını Instagram&apos;da paylaşıyoruz.
          Yeni paylaşımlar için bizi takip edin.
        </p>
        <a
          href={CONTACT.instagramHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="home-instagram-cta"
          className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-full text-sm font-semibold border border-[color:var(--em-espresso)] text-[color:var(--em-espresso)] hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors"
        >
          <Instagram size={16} /> {CONTACT.instagramHandle}
        </a>
      </section>

      {/* CONTACT STRIP */}
      <section className="border-t" style={{ borderColor: "var(--em-border)", background: "var(--em-dark-hero)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow" style={{ color: "#E8B59A" }}>İletişim</div>
            <h2 className="font-serif mt-3 text-3xl lg:text-4xl font-semibold" style={{ color: "var(--em-cream)" }}>
              Hemen iletişime geçelim.
            </h2>
            <p className="mt-4 max-w-xl" style={{ color: "#D7C9BA" }}>
              Sorularınız için en hızlı yanıt WhatsApp hattımızdan. Dilerseniz telefonla da
              doğrudan görüşebiliriz.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
            <a href={WHATSAPP_TRAINING_HREF} target="_blank" rel="noopener noreferrer" className="btn-terracotta flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-sm font-semibold" data-testid="home-contact-whatsapp">
              <MessageCircle size={16} /> WhatsApp&apos;tan Yaz
            </a>
            <a href={CONTACT.phoneHref} className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-sm font-semibold border transition-colors" style={{ borderColor: "var(--em-cream)", color: "var(--em-cream)" }} data-testid="home-contact-phone">
              <Phone size={16} /> {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
